import React, { useEffect, useState } from 'react';
import Xgplayer from 'xgplayer-react';
import heic2any from "heic2any";
import { saveAs } from 'file-saver';
import Viewer from 'react-viewer';

import Header from './Header';
import Footer from './Footer';
import PdfView from './PdfView';
import Component404 from './Component404';
import * as Helper from '../Helper'

import "./App.css"

// Get the backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Function to check if pathname returns JSON with a URL
async function checkForJsonUrl() {
  try {
    // Use the full URL including origin
    const fullUrl = window.location.origin + window.location.pathname;
    const response = await fetch(fullUrl);

    // Check if response is OK
    if (!response.ok) {
      return null;
    }

    // Read response as text first
    const text = await response.text();

    // Skip if response is HTML (likely the index.html redirect from Netlify)
    const trimmedText = text.trim();
    if (trimmedText.startsWith('<!DOCTYPE') || trimmedText.startsWith('<html') || trimmedText.startsWith('<!')) {
      return null;
    }

    // Check if text looks like JSON (starts with { or [)
    if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
      return null;
    }

    // Try to parse as JSON
    try {
      const data = JSON.parse(text);
      // If JSON has a url field, return it
      if (data.url && typeof data.url === 'string') {
        return data.url;
      }
    } catch (parseError) {
      // Not valid JSON, return null
      return null;
    }

    return null;
  } catch (error) {
    // If fetch fails, return null
    console.log('checkForJsonUrl error:', error);
    return null;
  }
}

// Function to get a signed URL from the backend
async function getSignedUrl(bucket, key) {
  if (!BACKEND_URL) {
    console.error('REACT_APP_BACKEND_URL is not configured');
    return null;
  }

  try {
    const response = await fetch(`${BACKEND_URL}/api/v1/public/sign/${bucket}/${key}`);
    const data = await response.json();

    if (data.success && data.url) {
      return data.url;
    }

    console.error('Failed to get signed URL');
    return null;
  } catch (error) {
    console.error('Error getting signed URL');
    return null;
  }
}

function App() {
  const [content, setContent] = useState("")
  const [visibleImg, setVisibleImg] = useState(true);

  // Inject styles to override xgplayer play icon - ensures it loads after library styles
  useEffect(() => {
    const styleId = 'xgplayer-play-icon-override';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .xgplayer-nostart.xgplayer-skin-default .xgplayer-start div .xgplayer-icon-play svg,
        .xgplayer-nostart.xgplayer-skin-default .xgplayer-start .xgplayer-icon-play svg,
        .xgplayer-nostart.xgplayer-skin-default .xgplayer-start div .xgplayer-icon-play svg path,
        .xgplayer-nostart.xgplayer-skin-default .xgplayer-start .xgplayer-icon-play svg path,
        .xgplayer-skin-default .xgplayer-start div .xgplayer-icon-play svg,
        .xgplayer-skin-default .xgplayer-start .xgplayer-icon-play svg,
        .xgplayer-skin-default .xgplayer-start div .xgplayer-icon-play svg path,
        .xgplayer-skin-default .xgplayer-start .xgplayer-icon-play svg path,
        .xgplayer-icon-play svg,
        .xgplayer-icon-play svg path {
          fill: #BF00FF !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Disable Ctrl+scroll (zoom/resize) functionality - completely ignore Ctrl button
  useEffect(() => {
    const handleWheel = (e) => {
      // Completely prevent Ctrl+scroll or Cmd+scroll
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    // Add event listener at capture phase to catch events early
    const options = { passive: false, capture: true };
    window.addEventListener('wheel', handleWheel, options);
    document.addEventListener('wheel', handleWheel, options);
    if (document.body) {
      document.body.addEventListener('wheel', handleWheel, options);
    }
    if (document.documentElement) {
      document.documentElement.addEventListener('wheel', handleWheel, options);
    }

    // Prevent Ctrl+Plus, Ctrl+Minus, Ctrl+0 zoom shortcuts
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0' || e.code === 'Equal' || e.code === 'Minus' || e.code === 'Digit0')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false, capture: true });
    document.addEventListener('keydown', handleKeyDown, { passive: false, capture: true });

    // Cleanup
    return () => {
      window.removeEventListener('wheel', handleWheel, options);
      document.removeEventListener('wheel', handleWheel, options);
      if (document.body) {
        document.body.removeEventListener('wheel', handleWheel, options);
      }
      if (document.documentElement) {
        document.documentElement.removeEventListener('wheel', handleWheel, options);
      }
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  useEffect(() => {
    (async () => {
      let pathname = window.location.pathname
      pathname = pathname.replace('index.html', '');
      pathname = pathname.replace(/^\/+|\/+$/g, '');
      pathname = pathname.replace('//', '/')
      let pathname_split = pathname.split('/')
      let s3_url = ""
      let s3_url_original = ""

      // First, check if the pathname itself returns JSON with a URL
      const jsonUrl = await checkForJsonUrl();
      if (jsonUrl) {
        s3_url = jsonUrl;
        s3_url_original = jsonUrl;
      } else if (pathname_split.length > 1) {
        // If no JSON URL found, fall back to existing logic
        // First part is the bucket, rest is the key
        const bucket = pathname_split[0]
        const key = pathname_split.slice(1).join('/')

        // Get signed URL from backend
        const signedUrl = await getSignedUrl(bucket, key);
        if (signedUrl) {
          s3_url = signedUrl;
          s3_url_original = signedUrl;
        }
      }

      //File ext
      const IMAGE_MIMETYPE = ['.jpg', '.JPG', '.jpeg', '.JPEG', '.png', '.PNG', '.heic', '.HEIC', '.heif', '.HEIF']
      const VIDEO_MIMETYPE = ['.mp4', '.MP4', '.mov', '.MOV']
      const PDF_MIMETYPE = ['.pdf']

      let file_ext = pathname.slice(pathname.lastIndexOf("."))
      let is_image = IMAGE_MIMETYPE.includes(file_ext)
      let is_video = VIDEO_MIMETYPE.includes(file_ext)
      let is_pdf = PDF_MIMETYPE.includes(file_ext)
      let w_height = window.innerHeight
      let header_height = document.getElementById("header-wrap").offsetHeight
      let mse_height = w_height - header_height

      let set_height = () => {
        w_height = window.innerHeight
        header_height = document.getElementById("header-wrap").offsetHeight
        mse_height = w_height - header_height

        document.getElementById("mse").style.height = `${mse_height}px`;
        document.getElementById("mse").style.marginTop = `${header_height}px`;
      }

      set_height()
      window.addEventListener("resize", (event) => {
        set_height()
      });

      let has_media = false

      // If we have a signed URL, use it directly
      if (s3_url) {
        if (is_video) {
          // Video player config - poster is optional now
          let config = {
            id: 'mse',
            url: s3_url,
            height: `${mse_height}px`,
            width: '100%'
          };
          let Player = null;

          setContent(<Xgplayer config={config} playerInit={(player) => { Player = player; }} />)

          has_media = true
        } else if (is_image) {
          let heic_format = ['.heic', '.HEIC', '.heif', '.HEIF']

          if (heic_format.includes(file_ext)) {
            let res = await fetch(s3_url)
            let blob = await res.blob()
            let conversionResult = await heic2any({ blob })
            s3_url = URL.createObjectURL(conversionResult);

          }

          if (s3_url) {
            const str_content = (
              <div className='flex justify-center items-center w-full h-full'>
                <Viewer
                  visible={visibleImg}
                  onClose={() => { setVisibleImg(false); }}
                  images={[{ src: s3_url, alt: '' }]}
                  noClose={true}
                  showTotal={false}
                  changeable={false}
                  noImgDetails={true}
                  noNavbar={true}
                  downloadable={false}
                />
              </div>
            )
            setContent(str_content)
            has_media = true
          }

        } else if (is_pdf) {
          document.getElementsByTagName("html")[0].style.overflowY = 'hidden'
          document.getElementsByTagName("body")[0].style.overflowY = 'hidden'
          setContent(<PdfView width="100%" height={mse_height} fileUrl={s3_url}></PdfView>)

          has_media = true
        }
      }

      if (!has_media) {
        setContent(<Component404></Component404>)
      } else {


        let elements = document.getElementsByClassName('btn-download')
        for (var x = 0; x < elements.length; x++) {
          elements[x].addEventListener("click", (event) => {
            event.preventDefault()

            let fileName = Helper.getFileName(s3_url_original);
            saveAs(s3_url_original, fileName);
          });
        }
      }


    })()

  }, []);

  const spin_style = {
    "maxWidth": "100px"
  }

  return (
    <div className="App">
      <Header></Header>

      <div id="mse" className='flex justify-center items-center'>
        <img style={spin_style} className={content ? "hidden" : ""} src="/images/common/spin.svg"></img>
        {content}
      </div>
      <Footer></Footer>
    </div>
  );
}

export default App;
